import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface DeleteAccountSectionProps {
  userEmail: string;
}

const DeleteAccountSection: React.FC<DeleteAccountSectionProps> = ({ userEmail }) => {
  const navigate = useNavigate();
  const [confirmEmail, setConfirmEmail] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (confirmEmail !== userEmail) {
      toast.error('Email does not match');
      return;
    }

    const confirmDelete = window.confirm(
      'Are you absolutely sure? This action cannot be undone and all your data will be permanently deleted.'
    );

    if (!confirmDelete) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId: user.id }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete account');
      }

      toast.success('Account deleted successfully');
      await supabase.auth.signOut();
      navigate('/');
    } catch (error: unknown) {
      console.error('Error deleting account:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete account. Please contact support.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <div className="flex">
          <AlertTriangle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-red-900 mb-1">Warning</h4>
            <p className="text-sm text-red-700">
              Deleting your account is permanent and cannot be undone. All your data will be deleted.
            </p>
          </div>
        </div>
      </div>

      {!showConfirm ? (
        <button
          onClick={() => setShowConfirm(true)}
          className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Delete Account
        </button>
      ) : (
        <form onSubmit={handleDeleteAccount} className="space-y-4">
          <div>
            <label htmlFor="confirm-email" className="block text-sm font-medium text-gray-700 mb-1">
              Type your email to confirm: <span className="font-semibold">{userEmail}</span>
            </label>
            <input
              id="confirm-email"
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder={userEmail}
              required
            />
          </div>

          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={loading || confirmEmail !== userEmail}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Deleting...' : 'Confirm Delete'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowConfirm(false);
                setConfirmEmail('');
              }}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default DeleteAccountSection;
